import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain, Sparkles, X as XIcon, CheckCircle2, Trophy, Frown,
  Target, Bell, Heart, Settings as SettingsIcon,
} from "lucide-react";
import api from "../lib/api";
import Modal from "./Modal";
import VoiceTextarea from "./VoiceTextarea";
import { useBranding } from "../context/TenantBrandingContext";

/**
 * CoachPill + CoachCheckinModal — Wave 19 #108.
 *
 * Personal Hidro Coach. Lives in the top app bar next to the
 * notification bell. Opens a fast, dedicated modal (NOT the slow main
 * Hidro chat) where the rep can dictate a daily check-in.
 *
 * Free-form text + voice (per user spec). On submit, the backend rolls
 * the check-in into the user's coaching profile, which is then injected
 * into every Hidro-generated text.
 */

const MOOD_OPTIONS = [
  { v: "energised", label: "Energised", emoji: "⚡" },
  { v: "steady",    label: "Steady",    emoji: "🌊" },
  { v: "tired",     label: "Tired",     emoji: "😴" },
  { v: "drained",   label: "Drained",   emoji: "🪫" },
  { v: "frustrated",label: "Frustrated", emoji: "😤" },
  { v: "proud",     label: "Proud",     emoji: "🦁" },
];

// ─── Top-bar pill ─────────────────────────────────────────────────────
export function CoachPill({ onOpen }) {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [status, setStatus] = useState(null);
  // Poll the nudge status on mount + once an hour. Cheap query.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/coach/nudge-status");
        if (!cancelled) setStatus(data);
      } catch (_e) { /* noop — pill still shows */ }
    };
    load();
    const t = setInterval(load, 60 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Pulse intensity. Local-time aware: nudge if it's past NUDGE_HOUR_LOCAL
  // and the rep hasn't checked in today.
  const visualState = useMemo(() => {
    if (!status) return { pulse: false, color: "neutral" };
    const lvl = status.nudge_level || "none";
    const hr = new Date().getHours();
    const nudgeHr = status.nudge_hour_local ?? 17;
    const pastNudge = hr >= nudgeHr;
    if (lvl === "loud") return { pulse: true, color: "amber" };       // 3+ days silent
    if (lvl === "first_time") return { pulse: true, color: "teal" };  // never checked in
    if (lvl === "soft" && pastNudge) return { pulse: true, color: "teal" }; // yesterday + past 5pm
    return { pulse: false, color: "neutral" };
  }, [status]);

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`relative inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border transition-colors ${
        visualState.color === "amber"
          ? "border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
          : visualState.color === "teal"
            ? "border-[#1FB6C1]/50 bg-[#1FB6C1]/10 text-[#1FB6C1] hover:bg-[#1FB6C1]/20"
            : "border-white/15 text-[#a0aabf] hover:border-[#A78BFA]/40 hover:text-[#C4B5FD]"
      }`}
      data-testid="coach-pill"
      title={
        visualState.color === "amber"
          ? "It's been a few days — let's debrief"
          : visualState.color === "teal"
            ? "Time for your daily check-in"
            : "Open " + aiName + " Coach"
      }
    >
      <Brain className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">Coach</span>
      {status?.streak_days > 0 && (
        <span
          className="text-[9px] font-mono opacity-80"
          title={`${status.streak_days}-day streak`}
        >
          {status.streak_days}🔥
        </span>
      )}
      {visualState.pulse && (
        <span
          className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${
            visualState.color === "amber" ? "bg-amber-400" : "bg-[#1FB6C1]"
          }`}
          style={{ animation: "hv-pulse 1.6s infinite" }}
        />
      )}
      <style>{`
        @keyframes hv-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
    </button>
  );
}

// ─── Check-in modal ───────────────────────────────────────────────────
export function CoachCheckinModal({ open, onClose, onSubmitted, source = "manual" }) {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [body, setBody] = useState("");
  const [mood, setMood] = useState("");
  const [voiceRaw, setVoiceRaw] = useState("");
  const [extracted, setExtracted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  // Last check-in for context display
  const [last, setLast] = useState(null);
  // Wave 20 #116 — surface the most recent §20 self-diagnosis so the
  // user sees what Hidro learned about them when they open the modal.
  const [recentDiagnosis, setRecentDiagnosis] = useState(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [profile, diags] = await Promise.all([
          api.get("/coach/profile"),
          api.get("/hidro/self-diagnose/recent?limit=1").catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        setLast(profile.data);
        setRecentDiagnosis((diags.data?.items || [])[0] || null);
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Voice capture is now handled inline by <VoiceTextarea>. When the rep
  // commits a dictation, we fan the extracted bundle into mood + chips
  // and store the raw transcript as metadata.

  const onVoiceCommit = ({ refined, raw, extracted: e }) => {
    // Replace body with the refined text (rep can still edit afterwards).
    setBody(refined || "");
    setVoiceRaw(raw || "");
    if (e) {
      if (e.mood && !mood) setMood(e.mood);
      setExtracted(e);
    }
  };

  const reset = () => {
    setBody("");
    setMood("");
    setVoiceRaw("");
    setExtracted(null);
    setErr("");
    setSaved(false);
  };

  const submit = async () => {
    if (!body.trim() && !voiceRaw) {
      setErr(`Tell ${aiName} something — even one sentence is fine.`);
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const payload = {
        body: body.trim(),
        voice_raw: voiceRaw || null,
        mood,
        summary: extracted?.summary || null,
        wins: extracted?.wins || [],
        losses: extracted?.losses || [],
        growth_areas: extracted?.growth_areas || [],
        reminders: extracted?.reminders || [],
        preference_signals: extracted?.preference_signals || [],
        source,
      };
      const { data } = await api.post("/coach/checkin", payload);
      setSaved(true);
      onSubmitted?.(data);
      // Close after a short success flash
      setTimeout(() => {
        reset();
        onClose?.();
      }, 1200);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save check-in");
    }
    setBusy(false);
  };

  return (
    <Modal
      open={!!open}
      onClose={() => { reset(); onClose?.(); }}
      title={(
        <span className="inline-flex items-center gap-2 w-full">
          <Brain className="w-4 h-4 text-[#C4B5FD]" />
          <span>{aiName} Coach</span>
          {last?.streak_days > 0 && (
            <span className="text-[10px] font-mono text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5">
              {last.streak_days}🔥
            </span>
          )}
          <Link
            to="/settings/hidro"
            onClick={() => onClose?.()}
            className="ml-auto inline-flex items-center gap-1 text-[10px] font-normal normal-case text-[#647087] hover:text-[#C4B5FD] transition-colors"
            title={`Tune how ${aiName} talks to you, what it remembers, and how it acts`}
            data-testid="coach-modal-settings-link"
          >
            <SettingsIcon className="w-3 h-3" /> Settings
          </Link>
        </span>
      )}
      testId="coach-checkin-modal"
      maxWidth="max-w-7xl"
    >
      <div className="space-y-3 text-sm">
        {/* The Hidro question prompt — adapts to context */}
        <div className="hv-card p-3 border-[#A78BFA]/30 bg-[#A78BFA]/5">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-[#C4B5FD] mt-0.5 shrink-0" />
            <div className="text-xs text-[#dde3ec] leading-relaxed space-y-1">
              <p>
                <strong className="text-[#C4B5FD]">How was your day?</strong> Any
                meaningful events, small wins or losses, anywhere you'd like to
                push yourself, or things you want me to remind you about?
              </p>
              <p className="text-[10px] text-[#a0aabf]">
                I learn from these — the more honest you are, the more I can
                shape future briefs and replies to fit your style.
              </p>
            </div>
          </div>
        </div>

        {/* Wave 20 #116 — Hidro's most recent self-diagnosis */}
        {recentDiagnosis && (
          <div
            className="border border-[#1FB6C1]/25 rounded-md p-3 bg-[#1FB6C1]/[0.04] text-xs"
            data-testid="coach-recent-diagnosis"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] font-bold mb-1 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> What Hidro noticed about you
            </div>
            <div className="text-[#dde3ec] leading-relaxed">{recentDiagnosis.summary}</div>
            <div className="text-[10px] text-[#647087] mt-1 font-mono">
              {(recentDiagnosis.created_at || "").slice(0, 16).replace("T", " ")}
            </div>
          </div>
        )}

        {/* Mood picker — always visible, one-tap */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1.5 font-bold">
            Mood (optional)
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="coach-mood-picker">
            {MOOD_OPTIONS.map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => setMood(mood === m.v ? "" : m.v)}
                className={`text-xs px-2 py-1 rounded-full border transition-colors flex items-center gap-1 ${
                  mood === m.v
                    ? "border-[#A78BFA]/50 bg-[#A78BFA]/15 text-[#C4B5FD]"
                    : "border-white/15 bg-white/[0.02] text-[#a0aabf] hover:border-white/25"
                }`}
                data-testid={`coach-mood-${m.v}`}
              >
                <span>{m.emoji}</span>
                <span>{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Free-form body — inline mic, identical pattern to MeetingDetailModal */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1.5 font-bold">
            Today's debrief
          </div>
          <VoiceTextarea
            value={body}
            onChange={setBody}
            onCommit={onVoiceCommit}
            context="personal_daily_debrief"
            rows={8}
            placeholder="Type freely or tap the mic — wins, losses, blockers, ideas, things I should remember…"
            testIdPrefix="coach-voice"
            data-testid="coach-body"
          />
        </div>

        {/* Auto-extracted preview — read-only chips */}
        {extracted && (extracted.wins?.length || extracted.losses?.length || extracted.growth_areas?.length || extracted.reminders?.length) > 0 && (
          <div className="hv-card p-2.5 bg-black/20" data-testid="coach-extracted-preview">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] mb-1.5 font-bold">
              Hidro picked these out — fine to save
            </div>
            <div className="space-y-1 text-[11px]">
              {extracted.wins?.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Trophy className="w-3 h-3 text-emerald-300 mt-0.5 shrink-0" />
                  <span>
                    <strong className="text-emerald-300">Wins:</strong>{" "}
                    {extracted.wins.join(" · ")}
                  </span>
                </div>
              )}
              {extracted.losses?.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Frown className="w-3 h-3 text-rose-300 mt-0.5 shrink-0" />
                  <span>
                    <strong className="text-rose-300">Losses:</strong>{" "}
                    {extracted.losses.join(" · ")}
                  </span>
                </div>
              )}
              {extracted.growth_areas?.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Target className="w-3 h-3 text-amber-300 mt-0.5 shrink-0" />
                  <span>
                    <strong className="text-amber-300">Growth:</strong>{" "}
                    {extracted.growth_areas.join(" · ")}
                  </span>
                </div>
              )}
              {extracted.reminders?.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Bell className="w-3 h-3 text-[#C4B5FD] mt-0.5 shrink-0" />
                  <span>
                    <strong className="text-[#C4B5FD]">Reminders:</strong>{" "}
                    {extracted.reminders.join(" · ")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {err && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
            {err}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[10px] text-[#647087]">
            {last?.last_checkin_at
              ? `Last check-in: ${new Date(last.last_checkin_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`
              : "First check-in — welcome 👋"}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { reset(); onClose?.(); }}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="coach-cancel"
            >
              <XIcon className="w-3 h-3" /> Close
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || saved || (!body.trim() && !voiceRaw)}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="coach-submit"
            >
              {saved ? <CheckCircle2 className="w-3 h-3 text-emerald-300" /> : <Heart className="w-3 h-3" />}
              {busy ? "Saving…" : saved ? "Saved" : "Save check-in"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
